import React, {createContext, useContext} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {SHA256} from 'crypto-js';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useUserSessionContext} from '../../../../common/components/core/UserSessionContext';

export type ChangePasswordFormValues = {
  oldPassword: string
  newPassword: string
  repeatNewPassword: string
}

export interface ChangePasswordFormContextProviderProps {
  children: React.ReactNode;
}

export const defaultContext = {
  initialValues: {
    oldPassword: '',
    newPassword: '',
    repeatNewPassword: '',
  },
};

export const ChangePasswordFormContext = createContext(defaultContext);
ChangePasswordFormContext.displayName = 'ChangePasswordFormContext';

export const useChangePasswordFormContext = () => useContext(ChangePasswordFormContext);

export const ChangePasswordFormContextProvider = ({children}: ChangePasswordFormContextProviderProps) => {
  const M = useMessage();
  const {apiService} = useGlobalContext();
  const {currentUser} = useUserSessionContext();
  const globalMessage = useGlobalMessageContext();
  const formik = useFormik<ChangePasswordFormValues>({
    initialValues: defaultContext.initialValues,
    validate: (values: ChangePasswordFormValues) => {
      let errors: Partial<ChangePasswordFormValues> = {};
      if (!values.oldPassword) {
        errors = {...errors, oldPassword: M.requiredField};
      }
      if (!values.newPassword) {
        errors = {...errors, newPassword: M.requiredField};
      }
      if (values.newPassword === values.oldPassword) {
        errors = {...errors, newPassword: M.newPasswordMustBeDifferentFromOld};
      }
      if (!values.repeatNewPassword) {
        errors = {...errors, repeatNewPassword: M.requiredField};
      }
      if (values.newPassword !== values.repeatNewPassword) {
        errors = {...errors, repeatNewPassword: M.newPasswordMismatch};
      }

      return errors;
    },
    onSubmit: async (values: ChangePasswordFormValues) => {
      try {
        const result = await apiService.changePassword(
          currentUser?._id || '',
          SHA256(values.oldPassword).toString(),
          SHA256(values.newPassword).toString(),
        );
        if (result.errorCode === 0) {
          globalMessage.open(M.passwordChangeSuccess);
        }
        if (result.errorCode < 0) {
          globalMessage.open(M.passwordIncorrect);
        }
      } catch (e) {
        console.error(e);
        globalMessage.open(M.serverError);
      }
    },
    enableReinitialize: true,
  });

  const context = {
    ...defaultContext
  };

  return (
    <ChangePasswordFormContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </ChangePasswordFormContext.Provider>
  );
};
